import type { UiMessageModule } from "./merge";

export const publicModelPageMessages = {
	en: {
		public: {
			common: {
				apiDocs: "API Docs",
				cacheRead: "Cache Read",
				clear: "Clear",
				clearAll: "Clear all",
				default: "Default",
				features: "Features",
				filtered: "filtered",
				filters: "Filters",
				grid: "Grid",
				home: "Home",
				in: "in",
				input: "Input",
				inputShort: "IN",
				modelIdLabel: "Model ID:",
				next: "Next",
				of: "of",
				official: "Official",
				out: "out",
				output: "Output",
				outputShort: "OUT",
				page: "Page",
				previous: "Previous",
				saved: "Saved",
				table: "Table",
				tokens: "Tokens",
				total: "Total",
				viaProvider: "via {provider}",
			},
			capabilities: {
				audioGeneration: "Speech Generation",
				cached: "Cached",
				json: "JSON",
				nativeWebSearch: "Native Web Search",
				reasoningBudget: "Reasoning Budget",
				speechGen: "Speech Gen",
				structuredJson: "Structured JSON",
				structuredJsonOutput: "Structured JSON Output",
				streaming: "Streaming",
				videoGen: "Video Gen",
			},
			models: {
				list: {
					defaultTitle: "Models",
					defaultDescription:
						"Comprehensive list of all supported models and their providers",
					searchPlaceholder: "Search models...",
					models: "Models",
					providers: "Providers",
					visionModels: "Vision Models",
					toolEnabled: "Tool-enabled",
					freeModels: "Free Models",
					useCase: "Use Case",
					allUseCases: "All Use Cases",
					codeGeneration: "Code Generation",
					chatAssistants: "Chat & Assistants",
					reasoningAnalysis: "Reasoning & Analysis",
					creativeWriting: "Creative & Writing",
					imageGeneration: "Image Generation",
					multimodalVision: "Multimodal (Vision)",
					provider: "Provider",
					allProviders: "All providers",
					inputPrice: "Input Price ($/M tokens)",
					outputPrice: "Output Price ($/M tokens)",
					contextSize: "Context Size (tokens)",
					minPrice: "Min price",
					maxPrice: "Max price",
					minSize: "Min size (e.g., 128000)",
					maxSize: "Max size (e.g., 200000)",
					additionalPricing: "Additional Pricing",
					videoSecondPricing: "Video per-second pricing",
					perCharacterPricing: "Per-character pricing (not per token)",
					perRequestPricing: "Per-request pricing (not per token)",
					perRequest: "Per Request",
					perSearch: "per search",
				},
				card: {
					supports: "Supports {capability}",
					fewerProviders: "Fewer providers",
					moreProvider: "{count} more provider",
					moreProviders: "{count} more providers",
					copyProviderModelId: "Copy provider/model ID",
					serviceTier: "Service tier",
					standardPricing: "Standard pricing (1x)",
					standardShort: "Standard",
					context: "Context:",
					perImage: "Per image",
					perImageWithResolution: "Per image ({resolution})",
					perCharacterPricing: "Per Character Pricing",
					inputText: "Input text",
					perThousandChars: "/1K chars",
					perSecondPricing: "Per Second Pricing",
					perSecond: "/sec",
					videoAudio: "Video / Audio",
					tieredPricing: "Tiered Pricing",
					tokenThresholdAbove: ">{count}K tokens",
					tokenThresholdUpTo: "<= {count}K tokens",
					imagePricing: "Image Pricing",
					imagePricingEstimated: "Image Pricing (est. per image)",
					imageOutput: "Image Output",
					perMillionTokens: "/M tokens",
					hideTokenPricing: "Hide token pricing",
					expandDetails: "Expand details",
					anySize: "any size",
					perRequest: "per request",
					perSearch: "per search",
				},
				selector: {
					searchPlaceholder: "Search models...",
					noModels: "No models found.",
					clearFilters: "Clear filters to see all models",
					modelFound: "{count} model found",
					modelsFound: "{count} models found",
					priceRange: "Price Range",
					allModels: "All models",
					freeModels: "Free models",
					lowCost: "Low cost (<= $0.000001)",
					mediumCost: "Medium cost (<= $0.00001)",
					highCost: "High cost (> $0.00001)",
				},
				rating: {
					error: "Error",
					failedSave: "Failed to save your rating.",
					makeRequests: "Make {minimum} requests to rate ({current}/{minimum})",
					noRatings: "No ratings yet",
					rateModel: "Rate this model",
					rating: "rating",
					ratings: "ratings",
					ratingRemoved: "Rating removed",
					ratingSaved: "Rating saved",
					remove: "Remove",
					saveRating: "Save rating",
					savedDescription: "Thanks for rating this model!",
					signInToRate: "Sign in to rate",
					star: "{count} star",
					stars: "{count} stars",
					youRated: "You rated {rating}/5",
				},
				codeExample: {
					copy: "Copy",
					copyFailed: "Copy failed",
					copyFailedDescription:
						"Could not copy to clipboard. Please try again.",
					copiedDescription:
						"{language} example has been copied to your clipboard.",
					copiedTitle: "Copied to clipboard",
					description:
						"Code examples for {model} using cURL, the OpenAI SDK, and the AI SDK.",
					example: "{language} example",
					title: "Use this model with doteb",
					viewExamples: "View code examples",
				},
				status: {
					deactivated: "Model Deactivated",
					deactivatedDescription:
						"All providers for this model have been deactivated. Requests will return errors.",
					deprecated: "Model Deprecated",
					deprecatedDescription:
						"All providers for this model have been deprecated. The model still works but may be removed in the future.",
					scheduledDeactivation: "Scheduled for Deactivation",
					scheduledDeactivationDescription:
						"All providers for this model are scheduled to be deactivated. Plan to migrate to an alternative model.",
					scheduledDeprecation: "Scheduled for Deprecation",
					scheduledDeprecationDescription:
						"All providers for this model are scheduled for deprecation. Consider migrating to an alternative model.",
				},
				benchmarks: {
					coding: "Coding",
					errors: "Errors",
					insufficientData:
						"Not enough traffic for reliable stats (under {requests} requests)",
					mostStable: "Most stable",
					performanceDescription:
						"Real performance data from doteb over the last {hours} hours. Higher uptime and throughput are better. Stats are hidden for providers with under {requests} requests, where the sample is too small to be reliable.",
					performanceTitle: "Provider Performance",
					qualityDescriptionPrefix: "Crowdsourced quality ratings from",
					qualityDescriptionSuffix:
						". Higher ELO score = better quality. Updated {updated}.",
					qualityTitle: "Quality Benchmarks",
					requests: "requests",
					throughput: "Throughput",
					throughputTitle:
						"Output tokens per second across all requests in the window",
					uptime: "Uptime",
					uptimeTitle: "Uptime over last {hours}h",
					overallText: "Overall (Text)",
				},
				compare: {
					cachedInputPrice: "Cached Input Price",
					description: "Description",
					downloadPng: "Download PNG",
					family: "Family",
					feature: "Feature",
					groups: {
						capabilities: "Capabilities",
						context: "Context",
						overview: "Overview",
						parameters: "Parameters",
						pricing: "Pricing",
					},
					imageInputPrice: "Image Input Price",
					inputPrice: "Input Price",
					maxContext: "Max Context",
					maxOutput: "Max Output",
					modelA: "Model A",
					modelB: "Model B",
					outputPrice: "Output Price",
					outputTypes: "Output Types",
					parallelToolCalls: "Parallel Tool Calls",
					released: "Released",
					requestPrice: "Request Price",
					selectModel: "Select a model",
					swapModels: "Swap Models",
					selectModelsTitle: "Compare AI Models",
					selectModelsDescription:
						"Select any two models from the directory to compare pricing, context, and capabilities.",
					supportedParameters: "Supported Parameters",
					toolCalling: "Tool Calling",
					tryInChat: "Try in Chat",
					poweredBy: "Powered by",
					viewDetailsFor: "View {model} details",
				},
			},
			timeline: {
				unknown: "Unknown",
				modelId: "Model ID:",
				viewDetails: "View model details",
			},
			uptimeMetrics: {
				errors: "Errors",
				latency: "Latency",
				throughput: "Throughput",
				uptime: "Uptime",
			},
			uptimeCharts: {
				avgDuration: "Avg Duration (ms)",
				avgTtft: "Avg TTFT",
				avgTtftMs: "Avg TTFT (ms)",
				cached: "Cached",
				client: "Client",
				duration: "{value}ms duration",
				gateway: "Gateway",
				lastFourHoursPoints: "Last 4 hours · {count} data points",
				metric: "Metric",
				noData:
					"No uptime data is available for this model in the last 4 hours.",
				noTraffic: "No traffic in the last 4 hours",
				rate: "{value}% rate",
				retry: "Retry",
				statsHidden: "stats hidden until {count} requests",
				unableLoad: "Unable to load uptime data. Please try again.",
				upstream: "Upstream",
				upstreamErrors: "Upstream errors",
			},
			tokenCalculator: {
				form: {
					officialWithProvider: "Official ({provider})",
					priceIn: "/M in",
					priceOut: "/M out",
				},
				results: {
					directFromProviders: "direct from providers",
					noMarkup: "cheapest provider per model, no markup",
					less: "{percent}% less",
					samePrice: "same price, more features",
					selectModelHint:
						"Select at least one model above to see your cost comparison.",
					shareSavings: "Share your {percent}% savings",
					shareResults: "Share these results",
					shareDescription:
						"Send this breakdown to your team or post it in one click.",
					copyLink: "Copy link",
					overpayingBy: "At official rates you're overpaying by",
					lessWithGateway: "{percent}% less with doteb, same models",
					basedOnVolumes: "Based on the token volumes entered above",
					startSaving: "Start saving for free",
					smartRouting: "Smart Routing",
					smartRoutingDescription:
						"Automatically routes to the cheapest available provider for each model",
					noMarkupTitle: "No Markup",
					noMarkupDescription:
						"Zero platform fees - you pay exactly what the provider charges",
					automaticFailover: "Automatic Failover",
					automaticFailoverDescription:
						"If one provider is down, requests automatically route to the next cheapest",
					shareTextTitle: "My LLM cost breakdown:",
					shareTextSaving: "Saving {percent}% with @llmgateway",
					shareTextCalculate: "Calculate yours:",
				},
				content: {
					understandingParagraph1:
						"Every large language model bills by the token, the small chunks of text a model reads and writes. Roughly speaking, one token is about four characters of English, so 1,000 tokens is around 750 words. Providers quote prices per million tokens, and they charge separately for the tokens you send (input) and the tokens the model generates (output).",
					understandingParagraph2:
						"Output tokens are usually two to four times more expensive than input tokens, so the ratio between your prompt size and response size has a big impact on your bill. A summarization workload that reads a lot and writes a little costs very differently from a code-generation workload that writes long responses. The calculator above keeps the two separate so your estimate reflects how you actually use each model.",
					understandingParagraph3:
						"Prices also vary by provider. A single popular model is often hosted by several providers at different rates, and those rates change as providers compete on price. Instead of locking yourself into one provider, doteb routes each request to the cheapest available provider for that model through one OpenAI-compatible API, with no platform markup. That is the gap the calculator shows: the official list price versus the lowest live price you would actually pay.",
					understandingParagraph4Prefix:
						"Use it to budget a new feature, compare GPT-4o against Claude or Gemini before you commit, or build the business case for switching providers. When the numbers look good, you can",
					understandingParagraph4Link: "start for free",
					understandingParagraph4Suffix:
						"and keep the same estimate in production.",
					faq: {
						q1: "What are input tokens and output tokens?",
						a1: "Input tokens are everything you send to the model, including your prompt, system message, and conversation history. Output tokens are what the model generates back. Output tokens almost always cost more than input tokens, which is why the split matters when you estimate spend.",
						q2: "Why can the same model have different prices?",
						a2: "Popular models are often served by several providers at different rates, and prices change as providers compete. doteb routes each request to the cheapest available provider for that model, so you pay the lowest live rate without changing any code.",
						q3: "Does doteb add a platform markup?",
						a3: "No. doteb shows provider pricing without a platform markup. You pay for model usage and can route to the cheapest provider available for each request.",
						q4: "How do I lower LLM API costs?",
						a4: "Route through a gateway that compares providers and picks the lowest price per request. Because doteb supports 200+ models behind one OpenAI-compatible API, you can switch models or providers based on cost without rewriting your integration.",
					},
				},
			},
		},
	},
	"zh-CN": {
		public: {
			common: {
				apiDocs: "API 文档",
				cacheRead: "缓存读取",
				clear: "清除",
				clearAll: "全部清除",
				default: "默认",
				features: "能力",
				filtered: "已筛选",
				filters: "筛选",
				grid: "网格",
				home: "首页",
				in: "输入",
				input: "输入",
				inputShort: "输入",
				modelIdLabel: "模型 ID：",
				next: "下一页",
				of: "/",
				official: "官方",
				out: "输出",
				output: "输出",
				outputShort: "输出",
				page: "第",
				previous: "上一页",
				saved: "已节省",
				table: "表格",
				tokens: "Token",
				total: "总计",
				viaProvider: "经由 {provider}",
			},
			capabilities: {
				audioGeneration: "语音生成",
				cached: "缓存",
				json: "JSON",
				nativeWebSearch: "原生网络搜索",
				reasoningBudget: "推理预算",
				speechGen: "语音生成",
				structuredJson: "结构化 JSON",
				structuredJsonOutput: "结构化 JSON 输出",
				streaming: "流式输出",
				videoGen: "视频生成",
			},
			models: {
				list: {
					defaultTitle: "模型",
					defaultDescription: "全部支持模型及其供应商的完整列表",
					searchPlaceholder: "搜索模型...",
					models: "模型",
					providers: "供应商",
					visionModels: "视觉模型",
					toolEnabled: "支持工具调用",
					freeModels: "免费模型",
					useCase: "使用场景",
					allUseCases: "全部使用场景",
					codeGeneration: "代码生成",
					chatAssistants: "聊天与助手",
					reasoningAnalysis: "推理与分析",
					creativeWriting: "创意与写作",
					imageGeneration: "图像生成",
					multimodalVision: "多模态（视觉）",
					provider: "供应商",
					allProviders: "全部供应商",
					inputPrice: "输入价格（$/百万 token）",
					outputPrice: "输出价格（$/百万 token）",
					contextSize: "上下文长度（token）",
					minPrice: "最低价格",
					maxPrice: "最高价格",
					minSize: "最小长度（如 128000）",
					maxSize: "最大长度（如 200000）",
					additionalPricing: "附加价格",
					videoSecondPricing: "视频按秒计费",
					perCharacterPricing: "按字符计费（不是按 token）",
					perRequestPricing: "按请求计费（不是按 token）",
					perRequest: "每次请求",
					perSearch: "每次搜索",
				},
				card: {
					supports: "支持 {capability}",
					fewerProviders: "收起供应商",
					moreProvider: "还有 {count} 个供应商",
					moreProviders: "还有 {count} 个供应商",
					copyProviderModelId: "复制供应商/模型 ID",
					serviceTier: "服务等级",
					standardPricing: "标准价格（1x）",
					standardShort: "标准",
					context: "上下文：",
					perImage: "每张图",
					perImageWithResolution: "每张图（{resolution}）",
					perCharacterPricing: "按字符计费",
					inputText: "输入文本",
					perThousandChars: "/千字符",
					perSecondPricing: "按秒计费",
					perSecond: "/秒",
					videoAudio: "视频 / 音频",
					tieredPricing: "阶梯价格",
					tokenThresholdAbove: ">{count}K token",
					tokenThresholdUpTo: "<= {count}K token",
					imagePricing: "图像价格",
					imagePricingEstimated: "图像价格（按张估算）",
					imageOutput: "图像输出",
					perMillionTokens: "/百万 token",
					hideTokenPricing: "隐藏 token 价格",
					expandDetails: "展开详情",
					anySize: "任意尺寸",
					perRequest: "每次请求",
					perSearch: "每次搜索",
				},
				selector: {
					searchPlaceholder: "搜索模型...",
					noModels: "未找到模型。",
					clearFilters: "清除筛选以查看全部模型",
					modelFound: "找到 {count} 个模型",
					modelsFound: "找到 {count} 个模型",
					priceRange: "价格范围",
					allModels: "全部模型",
					freeModels: "免费模型",
					lowCost: "低成本（<= $0.000001）",
					mediumCost: "中等成本（<= $0.00001）",
					highCost: "高成本（> $0.00001）",
				},
				rating: {
					error: "错误",
					failedSave: "保存评分失败。",
					makeRequests: "发起 {minimum} 次请求后可评分（{current}/{minimum}）",
					noRatings: "暂无评分",
					rateModel: "为该模型评分",
					rating: "条评分",
					ratings: "条评分",
					ratingRemoved: "评分已移除",
					ratingSaved: "评分已保存",
					remove: "移除",
					saveRating: "保存评分",
					savedDescription: "感谢你为该模型评分。",
					signInToRate: "登录后评分",
					star: "{count} 星",
					stars: "{count} 星",
					youRated: "你已评分 {rating}/5",
				},
				codeExample: {
					copy: "复制",
					copyFailed: "复制失败",
					copyFailedDescription: "无法复制到剪贴板，请重试。",
					copiedDescription: "{language} 示例已复制到剪贴板。",
					copiedTitle: "已复制到剪贴板",
					description: "{model} 的代码示例，包含 cURL、OpenAI SDK 和 AI SDK。",
					example: "{language} 示例",
					title: "通过 doteb 使用该模型",
					viewExamples: "查看代码示例",
				},
				status: {
					deactivated: "模型已停用",
					deactivatedDescription:
						"该模型的所有供应商都已停用，请求将返回错误。",
					deprecated: "模型已弃用",
					deprecatedDescription:
						"该模型的所有供应商都已弃用。模型仍可使用，但未来可能移除。",
					scheduledDeactivation: "计划停用",
					scheduledDeactivationDescription:
						"该模型的所有供应商都计划停用。请准备迁移到替代模型。",
					scheduledDeprecation: "计划弃用",
					scheduledDeprecationDescription:
						"该模型的所有供应商都计划弃用。建议迁移到替代模型。",
				},
				benchmarks: {
					coding: "编码",
					errors: "错误",
					insufficientData: "流量不足，无法可靠统计（低于 {requests} 次请求）",
					mostStable: "最稳定",
					performanceDescription:
						"来自 doteb 最近 {hours} 小时的真实性能数据。可用率和吞吐越高越好。低于 {requests} 次请求的供应商会隐藏统计，因为样本量太小。",
					performanceTitle: "供应商性能",
					qualityDescriptionPrefix: "来自",
					qualityDescriptionSuffix:
						"的众包质量评分。ELO 越高质量越好。更新于 {updated}。",
					qualityTitle: "质量基准",
					requests: "次请求",
					throughput: "吞吐",
					throughputTitle: "窗口内所有请求的输出 token 每秒速度",
					uptime: "可用率",
					uptimeTitle: "最近 {hours} 小时可用率",
					overallText: "整体（文本）",
				},
				compare: {
					cachedInputPrice: "缓存输入价格",
					description: "描述",
					downloadPng: "下载 PNG",
					family: "系列",
					feature: "特性",
					groups: {
						capabilities: "能力",
						context: "上下文",
						overview: "概览",
						parameters: "参数",
						pricing: "价格",
					},
					imageInputPrice: "图像输入价格",
					inputPrice: "输入价格",
					maxContext: "最大上下文",
					maxOutput: "最大输出",
					modelA: "模型 A",
					modelB: "模型 B",
					outputPrice: "输出价格",
					outputTypes: "输出类型",
					parallelToolCalls: "并行工具调用",
					released: "发布时间",
					requestPrice: "请求价格",
					selectModel: "选择模型",
					swapModels: "交换模型",
					selectModelsTitle: "比较 AI 模型",
					selectModelsDescription:
						"从模型目录中选择任意两个模型，比较价格、上下文和能力。",
					supportedParameters: "支持参数",
					toolCalling: "工具调用",
					tryInChat: "在聊天中试用",
					poweredBy: "技术支持：",
					viewDetailsFor: "查看 {model} 详情",
				},
			},
			timeline: {
				unknown: "未知",
				modelId: "模型 ID：",
				viewDetails: "查看模型详情",
			},
			uptimeMetrics: {
				errors: "错误",
				latency: "延迟",
				throughput: "吞吐",
				uptime: "可用率",
			},
			uptimeCharts: {
				avgDuration: "平均耗时（ms）",
				avgTtft: "平均 TTFT",
				avgTtftMs: "平均 TTFT（ms）",
				cached: "缓存",
				client: "客户端",
				duration: "{value}ms 总耗时",
				gateway: "网关",
				lastFourHoursPoints: "最近 4 小时 · {count} 个数据点",
				metric: "指标",
				noData: "最近 4 小时该模型暂无可用率数据。",
				noTraffic: "最近 4 小时无流量",
				rate: "{value}% 比率",
				retry: "重试",
				statsHidden: "达到 {count} 次请求后显示统计",
				unableLoad: "无法加载可用率数据，请重试。",
				upstream: "上游",
				upstreamErrors: "上游错误",
			},
			tokenCalculator: {
				form: {
					officialWithProvider: "官方（{provider}）",
					priceIn: "/百万输入",
					priceOut: "/百万输出",
				},
				results: {
					directFromProviders: "供应商直连价格",
					noMarkup: "每个模型选择最低价供应商，无平台加价",
					less: "少 {percent}%",
					samePrice: "价格相同，能力更多",
					selectModelHint: "至少选择一个模型后即可查看成本对比。",
					shareSavings: "分享你节省的 {percent}%",
					shareResults: "分享这些结果",
					shareDescription: "把这份成本拆解发送给团队，或一键发布。",
					copyLink: "复制链接",
					overpayingBy: "按官方价格你会多支付",
					lessWithGateway: "使用 doteb 少 {percent}%，模型不变",
					basedOnVolumes: "基于上方输入的 token 用量计算",
					startSaving: "免费开始节省",
					smartRouting: "智能路由",
					smartRoutingDescription: "自动为每个模型路由到当前可用的最低价供应商",
					noMarkupTitle: "无加价",
					noMarkupDescription: "零平台费，只支付供应商实际收取的费用",
					automaticFailover: "自动故障转移",
					automaticFailoverDescription:
						"当某个供应商不可用时，请求会自动路由到下一个最低价供应商",
					shareTextTitle: "我的 LLM 成本拆解：",
					shareTextSaving: "通过 @llmgateway 节省 {percent}%",
					shareTextCalculate: "计算你的成本：",
				},
				content: {
					understandingParagraph1:
						"大型语言模型按 token 计费，token 是模型读取和写出的文本片段。粗略来说，一个英文 token 约等于四个字符，1,000 个 token 大约是 750 个单词。供应商通常按百万 token 报价，并分别计算你发送的 token（输入）和模型生成的 token（输出）。",
					understandingParagraph2:
						"输出 token 通常比输入 token 贵两到四倍，因此 prompt 长度和响应长度的比例会显著影响账单。读取很多、写出很少的摘要任务，与写出长回复的代码生成任务成本完全不同。上方计算器会把两者分开，让估算贴近你的真实用法。",
					understandingParagraph3:
						"价格也会因供应商而异。同一个热门模型常由多个供应商以不同价格提供，这些价格也会随着竞争而变化。与其锁定某个供应商，不如通过 doteb 用一个 OpenAI 兼容 API，把每个请求路由到该模型当前可用的最低价供应商，且没有平台加价。计算器展示的正是官方标价与实际最低价之间的差距。",
					understandingParagraph4Prefix:
						"你可以用它为新功能做预算，在接入前比较 GPT-4o、Claude 或 Gemini，也可以为切换供应商建立商业依据。当数字合适时，你可以",
					understandingParagraph4Link: "免费开始",
					understandingParagraph4Suffix: "并在生产环境沿用同一估算口径。",
					faq: {
						q1: "什么是输入 token 和输出 token？",
						a1: "输入 token 是你发送给模型的所有内容，包括 prompt、system message 和会话历史。输出 token 是模型返回生成的内容。输出 token 几乎总是比输入 token 更贵，因此估算花费时必须区分两者。",
						q2: "为什么同一个模型会有不同价格？",
						a2: "热门模型通常由多个供应商以不同费率提供，价格也会随着竞争变化。doteb 会把每个请求路由到该模型当前可用的最低价供应商，因此无需改代码也能按最低实时价格付费。",
						q3: "doteb 会增加平台加价吗？",
						a3: "不会。doteb 展示的是无平台加价的供应商价格。你只为模型用量付费，并可为每个请求路由到最低价供应商。",
						q4: "如何降低 LLM API 成本？",
						a4: "使用能够比较供应商并按请求选择最低价的 gateway。由于 doteb 通过一个 OpenAI 兼容 API 支持 200+ 模型，你可以按成本切换模型或供应商，而无需重写集成。",
					},
				},
			},
		},
	},
} satisfies UiMessageModule;
